//src/admin/pages/AdminImportProducts.js
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FaBoxOpen,
  FaCheck,
  FaExclamationTriangle,
  FaSearch,
  FaSyncAlt,
} from "react-icons/fa";
import {
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
} from "react-icons/fi";

import { useSearchParams } from "react-router-dom";

import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { AdminLanguageContext } from "../context/AdminLanguageContext";

import {
  getEtikaImportProducts,
  importEtikaProducts,
  syncEtikaImportCategories,
} from "../../services/adminProductImports.api";

const PER_PAGE = 10;
const MAX_BULK_IMPORT = 25;

const isVideoFile = (url = "") =>
  /\.(mp4|webm|ogg|mov)$/i.test(
    String(url).split("?")[0]
  );

const ProductThumbnail = ({ src, name }) => {
  if (isVideoFile(src)) {
    return (
      <video
        src={src}
        muted
        playsInline
        preload="metadata"
        className="w-11 h-11 rounded-lg object-cover bg-gray-100"
      />
    );
  }

  return (
    <img
      src={src || "/no-image.png"}
      alt={name || ""}
      className="w-11 h-11 rounded-lg object-cover bg-gray-100"
      onError={(event) => {
        event.currentTarget.src = "/no-image.png";
      }}
    />
  );
};

function AdminImportProducts() {
  const { lang } = useContext(
    AdminLanguageContext
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const [isOpen, setIsOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    ready: 0,
    already_imported: 0,
    blocked: 0,
  });

const [categoryOpen, setCategoryOpen] =
  useState(false);

const [statusOpen, setStatusOpen] =
  useState(false);

  const [loading, setLoading] = useState(true);
  const [importing, setImporting] =
    useState(false);
  const [syncingCategories, setSyncingCategories] =
    useState(false);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] =
    useState("all");
  const [statusFilter, setStatusFilter] =
    useState("ready");

  const [selectedIds, setSelectedIds] =
    useState([]);

  const ADMIN_IMPORT_PRODUCTS_PAGE_KEY = "adminImportProductsCurrentPage";

  const getValidPage = (value) => {
    const n = Number(value || 1);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
  };

  const getInitialPage = () => {
    const urlPage = searchParams.get("page");
    const savedPage = sessionStorage.getItem(ADMIN_IMPORT_PRODUCTS_PAGE_KEY);
    return getValidPage(urlPage || savedPage || 1);
  };

  const [page, setPage] = useState(getInitialPage);

  useEffect(() => {
    const urlPage = searchParams.get("page");
    const savedPage = sessionStorage.getItem(ADMIN_IMPORT_PRODUCTS_PAGE_KEY);
    const nextPage = getValidPage(urlPage || savedPage || 1);

    if (nextPage !== page) {
      setPage(nextPage);
    }

    if (!urlPage && nextPage > 1) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("page", String(nextPage));
        return params;
      });
    }
  }, [searchParams, page, setSearchParams]);

  const goToImportProductsPage = (nextPage) => {
    const rawNext = typeof nextPage === "function" ? nextPage(page) : nextPage;
    const safePage = getValidPage(rawNext);

    setPage(safePage);

    if (safePage <= 1) {
      sessionStorage.removeItem(ADMIN_IMPORT_PRODUCTS_PAGE_KEY);
    } else {
      sessionStorage.setItem(ADMIN_IMPORT_PRODUCTS_PAGE_KEY, String(safePage));
    }

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);

      if (safePage <= 1) {
        params.delete("page");
      } else {
        params.set("page", String(safePage));
      }

      return params;
    });
  };

  const didMountImportFiltersRef = useRef(false);

  const [message, setMessage] = useState(null);
  const [resultModal, setResultModal] =
    useState(null);

  const adminUser = JSON.parse(
    localStorage.getItem("adminUser") || "{}"
  );

  const perms = JSON.parse(
    localStorage.getItem("staffPermissions") ||
      "{}"
  );

  const canAccess =
    adminUser.role === "admin" ||
    perms.can_manage_products;

  const TEXT =
    {
      EN: {
        title: "Import Products",
        subtitle:
          "Import products from Etika Wholesale into CHIACA.",
        refresh: "Refresh",
        syncCategories: "Sync collections",
        importSelected: "Import selected",
        search: "Search products...",
        allCategories: "All collections",
        ready: "Ready",
        blocked: "Blocked",
        imported: "Imported",
        all: "All",
        product: "Product",
        publicId: "Public ID",
        category: "Collection",
        price: "Price",
        stock: "Stock",
        variants: "Variants",
        status: "Status",
        loading: "Loading Etika products...",
        empty: "No products found.",
        selectLimit:
          "You can select a maximum of 25 products at once.",
        selected: "selected",
        importedSuccessfully:
          "Products imported successfully.",
        partialImport:
          "Some products were imported and some failed.",
        noSelection:
          "Select at least one ready product.",
        close: "Close",
        failed: "Failed",
        successful: "Successful",
        missingCategory: "Missing collection",
        noVariants: "No variants",
        publicIdExists: "Already exists",
        missingMedia: "Missing media",
        alreadyImported: "Imported",
        readyStatus: "Ready",
        accessDenied:
          "You are not allowed to access this page.",
        previous: "Previous",
        next: "Next",
      },
      AR: {
        title: "استيراد المنتجات",
        subtitle:
          "استيراد المنتجات من Etika Wholesale إلى CHIACA.",
        refresh: "تحديث",
        syncCategories: "مزامنة المجموعات",
        importSelected: "استيراد المحدد",
        search: "البحث عن المنتجات...",
        allCategories: "كل المجموعات",
        ready: "جاهز",
        blocked: "محظور",
        imported: "تم استيراده",
        all: "الكل",
        product: "المنتج",
        publicId: "رمز المنتج",
        category: "المجموعة",
        price: "السعر",
        stock: "المخزون",
        variants: "المتغيرات",
        status: "الحالة",
        loading: "جاري تحميل منتجات Etika...",
        empty: "لا توجد منتجات.",
        selectLimit:
          "يمكن تحديد 25 منتجاً كحد أقصى.",
        selected: "محدد",
        importedSuccessfully:
          "تم استيراد المنتجات بنجاح.",
        partialImport:
          "تم استيراد بعض المنتجات وفشل البعض.",
        noSelection:
          "حدد منتجاً جاهزاً واحداً على الأقل.",
        close: "إغلاق",
        failed: "فشل",
        successful: "نجح",
        missingCategory: "المجموعة غير موجودة",
        noVariants: "لا توجد متغيرات",
        publicIdExists: "موجود مسبقاً",
        missingMedia: "الوسائط غير موجودة",
        alreadyImported: "تم الاستيراد",
        readyStatus: "جاهز",
        accessDenied:
          "غير مسموح لك بالدخول إلى هذه الصفحة.",
        previous: "السابق",
        next: "التالي",
      },
    }[lang];

  const loadProducts = async () => {
    setLoading(true);
    setMessage(null);

    try {
      const data =
        await getEtikaImportProducts();

      setProducts(data.products || []);
      setSummary(
        data.summary || {
          total: 0,
          ready: 0,
          already_imported: 0,
          blocked: 0,
        }
      );

      setSelectedIds((previous) =>
        previous.filter((id) =>
          (data.products || []).some(
            (product) =>
              product.source_product_id === id &&
              product.ready_to_import
          )
        )
      );
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

useEffect(() => {
  const handleOutsideClick = (event) => {
    if (
      !event.target.closest(".import-category-dropdown")
    ) {
      setCategoryOpen(false);
    }

    if (
      !event.target.closest(".import-status-dropdown")
    ) {
      setStatusOpen(false);
    }
  };

  if (categoryOpen || statusOpen) {
    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );
  }

  return () =>
    document.removeEventListener(
      "mousedown",
      handleOutsideClick
    );
}, [categoryOpen, statusOpen]);

  useEffect(() => {
    if (canAccess) {
      loadProducts();
    }
  }, [canAccess]);

  useEffect(() => {
    if (!didMountImportFiltersRef.current) {
      didMountImportFiltersRef.current = true;
      return;
    }

    goToImportProductsPage(1);
  }, [search, categoryFilter, statusFilter]);

  const categories = useMemo(() => {
    const map = new Map();

    products.forEach((product) => {
      (product.categories || []).forEach(
        (category) => {
          const key = String(
            category.slug || ""
          ).toLowerCase();

          if (!key || map.has(key)) return;

          map.set(key, {
            slug: category.slug,
            name_en:
              category.name_en ||
              category.slug,
            name_ar:
              category.name_ar ||
              category.name_en ||
              category.slug,
          });
        }
      );
    });

    return Array.from(map.values()).sort(
      (a, b) =>
        String(a.name_en).localeCompare(
          String(b.name_en)
        )
    );
  }, [products]);

  const filteredProducts = useMemo(() => {
    const query = search
      .trim()
      .toLowerCase();

    return products.filter((product) => {
      const categorySlugs = (
        product.categories || []
      ).map((category) =>
        String(category.slug || "").toLowerCase()
      );

      const matchesSearch =
        !query ||
        String(product.name_en || "")
          .toLowerCase()
          .includes(query) ||
        String(product.name_ar || "")
          .toLowerCase()
          .includes(query) ||
        String(product.public_id || "")
          .toLowerCase()
          .includes(query) ||
        categorySlugs.some((slug) =>
          slug.includes(query)
        );

      const matchesCategory =
        categoryFilter === "all" ||
        categorySlugs.includes(
          categoryFilter.toLowerCase()
        );

      let matchesStatus = true;

      if (statusFilter === "ready") {
        matchesStatus =
          product.ready_to_import;
      } else if (statusFilter === "blocked") {
        matchesStatus =
          !product.ready_to_import &&
          !product.already_imported;
      } else if (statusFilter === "imported") {
        matchesStatus =
          product.already_imported;
      }

      return (
        matchesSearch &&
        matchesCategory &&
        matchesStatus
      );
    });
  }, [
    products,
    search,
    categoryFilter,
    statusFilter,
  ]);

  const totalPages = Math.max(
    1,
    Math.ceil(
      filteredProducts.length / PER_PAGE
    )
  );

  useEffect(() => {
    if (loading) return;

    if (page > totalPages) {
      goToImportProductsPage(totalPages);
    }
  }, [loading, page, totalPages]);

  const paginatedProducts =
    filteredProducts.slice(
      (page - 1) * PER_PAGE,
      page * PER_PAGE
    );

  const selectablePageIds =
    paginatedProducts
      .filter(
        (product) =>
          product.ready_to_import
      )
      .map(
        (product) =>
          product.source_product_id
      );

  const allPageSelected =
    selectablePageIds.length > 0 &&
    selectablePageIds.every((id) =>
      selectedIds.includes(id)
    );

  const toggleProduct = (product) => {
    if (!product.ready_to_import) return;

    const id = product.source_product_id;

    setSelectedIds((previous) => {
      if (previous.includes(id)) {
        return previous.filter(
          (item) => item !== id
        );
      }

      if (
        previous.length >= MAX_BULK_IMPORT
      ) {
        setMessage({
          type: "error",
          text: TEXT.selectLimit,
        });

        return previous;
      }

      return [...previous, id];
    });
  };

  const toggleCurrentPage = () => {
    setSelectedIds((previous) => {
      if (allPageSelected) {
        return previous.filter(
          (id) =>
            !selectablePageIds.includes(id)
        );
      }

      const next = [
        ...new Set([
          ...previous,
          ...selectablePageIds,
        ]),
      ];

      if (next.length > MAX_BULK_IMPORT) {
        setMessage({
          type: "error",
          text: TEXT.selectLimit,
        });

        return previous;
      }

      return next;
    });
  };

  const handleImport = async () => {
    if (!selectedIds.length) {
      setMessage({
        type: "error",
        text: TEXT.noSelection,
      });

      return;
    }

    setImporting(true);
    setMessage(null);

    try {
      const response =
        await importEtikaProducts(
          selectedIds
        );

      const data = response.data;

      setResultModal(data);

      setMessage({
        type:
          data.failed_count > 0
            ? "warning"
            : "success",
        text:
          data.failed_count > 0
            ? TEXT.partialImport
            : TEXT.importedSuccessfully,
      });

      setSelectedIds([]);
      await loadProducts();
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message,
      });
    } finally {
      setImporting(false);
    }
  };

  const handleSyncCategories = async () => {
    setSyncingCategories(true);
    setMessage(null);

    try {
      const response =
        await syncEtikaImportCategories();

      setMessage({
        type: "success",
        text:
          lang === "AR"
            ? `تم إنشاء ${response.data.created_count || 0} مجموعة جديدة.`
            : `${response.data.created_count || 0} new collections created.`,
      });

      await loadProducts();
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message,
      });
    } finally {
      setSyncingCategories(false);
    }
  };

  const getStatus = (product) => {
    if (product.already_imported) {
      return {
        label: TEXT.alreadyImported,
        className:
          "bg-blue-50 text-blue-700 border-blue-200",
      };
    }

    if (product.ready_to_import) {
      return {
        label: TEXT.readyStatus,
        className:
          "bg-green-50 text-green-700 border-green-200",
      };
    }

    const reasons =
      product.blocking_reasons || [];

    if (
      reasons.includes("public_id_exists")
    ) {
      return {
        label: TEXT.publicIdExists,
        className:
          "bg-gray-100 text-gray-700 border-gray-200",
      };
    }

    if (reasons.includes("no_variants")) {
      return {
        label: TEXT.noVariants,
        className:
          "bg-amber-50 text-amber-700 border-amber-200",
      };
    }

    if (
      reasons.includes("missing_category")
    ) {
      return {
        label: TEXT.missingCategory,
        className:
          "bg-red-50 text-red-700 border-red-200",
      };
    }

    if (
      reasons.includes("missing_media_file") ||
      reasons.includes("no_media")
    ) {
      return {
        label: TEXT.missingMedia,
        className:
          "bg-red-50 text-red-700 border-red-200",
      };
    }

    return {
      label: TEXT.blocked,
      className:
        "bg-red-50 text-red-700 border-red-200",
    };
  };

const selectedCategoryLabel =
  categoryFilter === "all"
    ? TEXT.allCategories
    : (() => {
        const category = categories.find(
          (item) =>
            String(item.slug).toLowerCase() ===
            String(categoryFilter).toLowerCase()
        );

        if (!category) {
          return TEXT.allCategories;
        }

        return lang === "AR"
          ? category.name_ar
          : category.name_en;
      })();

const selectedStatusLabel =
  {
    ready: TEXT.ready,
    blocked: TEXT.blocked,
    imported: TEXT.imported,
    all: TEXT.all,
  }[statusFilter] || TEXT.ready;

  return (
    <div
      className={`flex bg-[#f1f1f1] min-h-screen ${
        isOpen ? "overflow-hidden" : ""
      }`}
    >
      <AdminSidebar
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />

      <div
        className={`flex-1 ${
          lang === "EN" ? "lg:ml-64" : ""
        } w-full transition-all duration-300`}
      >
        <AdminHeader setIsOpen={setIsOpen} />

        <main
          className="p-4 sm:p-6 animate-fadeSlide"
          dir={lang === "AR" ? "rtl" : "ltr"}
        >
          {!canAccess ? (
            <div className="mt-10 bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <p className="text-red-700 font-semibold text-lg">
                {TEXT.accessDenied}
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                <div>
                  <div className="mx-1 flex items-center gap-2">
  <div className="flex items-center justify-center w-6 h-6">
    <FiDownload className="w-[18px] h-[18px] text-[#202223]" />
  </div>

  <h1 className="text-[20px] font-semibold text-gray-900">
    {TEXT.title}
  </h1>
</div>

                  <p className="text-sm text-gray-500 mt-1">
                    {TEXT.subtitle}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={handleSyncCategories}
                    disabled={syncingCategories}
                    className="h-9 px-3 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-medium flex items-center gap-2 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <FaSyncAlt
                      className={
                        syncingCategories
                          ? "animate-spin"
                          : ""
                      }
                    />
                    {TEXT.syncCategories}
                  </button>

                  <button
                    onClick={loadProducts}
                    disabled={loading}
                    className="h-9 px-3 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-medium flex items-center gap-2 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <FaSyncAlt
                      className={
                        loading
                          ? "animate-spin"
                          : ""
                      }
                    />
                    {TEXT.refresh}
                  </button>

                  <button
                    onClick={handleImport}
                    disabled={
                      importing ||
                      selectedIds.length === 0
                    }
                    className="h-9 px-4 bg-[#1f1f1f] text-white rounded-lg shadow-sm text-sm font-medium flex items-center gap-2 hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <FiDownload />
                    {importing
                      ? lang === "AR"
                        ? "جاري الاستيراد..."
                        : "Importing..."
                      : `${TEXT.importSelected} (${selectedIds.length})`}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                {[
                  {
                    label: TEXT.all,
                    value: summary.total,
                  },
                  {
                    label: TEXT.ready,
                    value: summary.ready,
                  },
                  {
                    label: TEXT.imported,
                    value:
                      summary.already_imported,
                  },
                  {
                    label: TEXT.blocked,
                    value: summary.blocked,
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="bg-white border border-[#e3e3e3] rounded-[14px] px-4 py-4 shadow-[0_8px_24px_rgba(0,0,0,0.07)]"
                  >
                    <div className="text-xs text-gray-500">
                      {item.label}
                    </div>

                    <div className="mt-1 text-2xl font-semibold text-gray-900">
                      {item.value || 0}
                    </div>
                  </div>
                ))}
              </div>

              {message && (
                <div
                  className={`mb-4 px-4 py-3 rounded-xl border text-sm ${
                    message.type === "success"
                      ? "bg-green-50 border-green-200 text-green-700"
                      : message.type === "warning"
                      ? "bg-amber-50 border-amber-200 text-amber-700"
                      : "bg-red-50 border-red-200 text-red-700"
                  }`}
                >
                  {message.text}
                </div>
              )}

             <div className="bg-white border border-[#e3e3e3] rounded-[14px] overflow-visible shadow-[0_12px_32px_rgba(0,0,0,0.10),0_2px_6px_rgba(0,0,0,0.06)]">
                <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 px-4 py-3 border-b border-[#e3e3e3] bg-white rounded-t-[14px] overflow-visible">
                  <div className="relative flex-1 max-w-xl h-8">
  <FaSearch
    className={`absolute top-1/2 -translate-y-1/2 text-gray-400 text-sm z-10 ${
      lang === "AR"
        ? "right-3"
        : "left-3"
    }`}
  />

  <input
    value={search}
    onChange={(event) =>
      setSearch(event.target.value)
    }
    placeholder={TEXT.search}
    className={`w-full h-8 bg-white border border-gray-300 rounded-lg outline-none shadow-sm focus:border-gray-500 focus:ring-2 focus:ring-gray-200 text-sm ${
      lang === "AR"
        ? "pr-9 pl-3"
        : "pl-9 pr-3"
    }`}
  />
</div>

                  <div className="flex flex-wrap gap-2">
  {/* COLLECTION DROPDOWN */}
  <div className="relative import-category-dropdown">
    <button
      type="button"
      onClick={() => {
        setCategoryOpen((value) => !value);
        setStatusOpen(false);
      }}
      className="
        h-9 min-w-[150px] px-3
        flex items-center justify-between gap-2
        bg-white
        border border-gray-300
        rounded-lg
        shadow-[0_1px_0_rgba(0,0,0,0.05),0_2px_4px_rgba(0,0,0,0.06)]
        text-sm font-medium text-gray-900
        hover:bg-gray-50
        transition
      "
    >
      <span className="truncate">
        {selectedCategoryLabel}
      </span>

      <FiChevronDown className="text-gray-500" />
    </button>

    {categoryOpen && (
      <div
        className={`
          absolute top-full mt-1
          ${
            lang === "AR"
              ? "left-0"
              : "right-0"
          }
          w-60
          bg-white
          border border-gray-200
          rounded-xl
          shadow-[0_20px_40px_rgba(0,0,0,0.14)]
          z-[9999]
          overflow-hidden
          animate-sortOverlay
        `}
      >
        <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b">
          {lang === "AR"
            ? "تصفية حسب المجموعة"
            : "Filter by collection"}
        </div>

        <button
          type="button"
          onClick={() => {
            setCategoryFilter("all");
            setCategoryOpen(false);
            goToImportProductsPage(1);
          }}
          className={`w-full px-4 py-2 text-sm hover:bg-gray-100 ${
            lang === "AR"
              ? "text-right"
              : "text-left"
          } ${
            categoryFilter === "all"
              ? "bg-gray-100 font-medium"
              : ""
          }`}
        >
          {TEXT.allCategories}
        </button>

        <div className="max-h-64 overflow-y-auto">
          {categories.map((category) => (
            <button
              type="button"
              key={category.slug}
              onClick={() => {
                setCategoryFilter(category.slug);
                setCategoryOpen(false);
                goToImportProductsPage(1);
              }}
              className={`w-full px-4 py-2 text-sm hover:bg-gray-100 ${
                lang === "AR"
                  ? "text-right"
                  : "text-left"
              } ${
                categoryFilter === category.slug
                  ? "bg-gray-100 font-medium"
                  : ""
              }`}
            >
              {lang === "AR"
                ? category.name_ar
                : category.name_en}
            </button>
          ))}
        </div>
      </div>
    )}
  </div>

  {/* STATUS DROPDOWN */}
  <div className="relative import-status-dropdown">
    <button
      type="button"
      onClick={() => {
        setStatusOpen((value) => !value);
        setCategoryOpen(false);
      }}
      className="
        h-9 min-w-[125px] px-3
        flex items-center justify-between gap-2
        bg-white
        border border-gray-300
        rounded-lg
        shadow-[0_1px_0_rgba(0,0,0,0.05),0_2px_4px_rgba(0,0,0,0.06)]
        text-sm font-medium text-gray-900
        hover:bg-gray-50
        transition
      "
    >
      <span>{selectedStatusLabel}</span>

      <FiChevronDown className="text-gray-500" />
    </button>

    {statusOpen && (
      <div
        className={`
          absolute top-full mt-1
          ${
            lang === "AR"
              ? "left-0"
              : "right-0"
          }
          w-44
          bg-white
          border border-gray-200
          rounded-xl
          shadow-[0_20px_40px_rgba(0,0,0,0.14)]
          z-[9999]
          overflow-hidden
          animate-sortOverlay
        `}
      >
        {[
          {
            value: "ready",
            label: TEXT.ready,
          },
          {
            value: "blocked",
            label: TEXT.blocked,
          },
          {
            value: "imported",
            label: TEXT.imported,
          },
          {
            value: "all",
            label: TEXT.all,
          },
        ].map((option) => (
          <button
            type="button"
            key={option.value}
            onClick={() => {
              setStatusFilter(option.value);
              setStatusOpen(false);
              goToImportProductsPage(1);
            }}
            className={`w-full px-4 py-2 text-sm hover:bg-gray-100 ${
              lang === "AR"
                ? "text-right"
                : "text-left"
            } ${
              statusFilter === option.value
                ? "bg-gray-100 font-medium"
                : ""
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    )}
  </div>
</div>
                </div>

                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full min-w-[900px] text-center">
                    <thead className="bg-[#f6f6f7] text-[#6d7175] text-[13px]">
                      <tr>
                        <th className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={
                              allPageSelected
                            }
                            disabled={
                              selectablePageIds.length ===
                              0
                            }
                            onChange={
                              toggleCurrentPage
                            }
                            className="w-4 h-4 accent-gray-800"
                          />
                        </th>
                        <th className="px-4 py-3">
                          {TEXT.product}
                        </th>
                        <th className="px-4 py-3">
                          {TEXT.publicId}
                        </th>
                        <th className="px-4 py-3">
                          {TEXT.category}
                        </th>
                        <th className="px-4 py-3">
                          {TEXT.price}
                        </th>
                        <th className="px-4 py-3">
                          {TEXT.stock}
                        </th>
                        <th className="px-4 py-3">
                          {TEXT.variants}
                        </th>
                        <th className="px-4 py-3">
                          {TEXT.status}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading ? (
                        [...Array(6)].map(
                          (_, index) => (
                            <tr
                              key={index}
                              className="border-b animate-pulse"
                            >
                              <td
                                colSpan="8"
                                className="px-4 py-5"
                              >
                                <div className="h-7 bg-gray-100 rounded" />
                              </td>
                            </tr>
                          )
                        )
                      ) : paginatedProducts.length ===
                        0 ? (
                        <tr>
                          <td
                            colSpan="8"
                            className="py-12"
                          >
                            <FaBoxOpen className="mx-auto text-3xl text-gray-300 mb-3" />
                            <p className="text-gray-500">
                              {TEXT.empty}
                            </p>
                          </td>
                        </tr>
                      ) : (
                        paginatedProducts.map(
                          (product) => {
                            const status =
                              getStatus(product);

                            return (
                              <tr
                                key={
                                  product.source_product_id
                                }
                                className="border-b border-gray-100 hover:bg-gray-50"
                              >
                                <td className="px-3 py-2">
                                  <input
                                    type="checkbox"
                                    disabled={
                                      !product.ready_to_import
                                    }
                                    checked={selectedIds.includes(
                                      product.source_product_id
                                    )}
                                    onChange={() =>
                                      toggleProduct(
                                        product
                                      )
                                    }
                                    className="w-4 h-4 accent-gray-800 disabled:opacity-30"
                                  />
                                </td>

                                <td className="px-4 py-2 text-left">
                                  <div className="flex items-center gap-3">
                                    <ProductThumbnail
                                      src={
                                        product.image
                                      }
                                      name={
                                        product.name_en
                                      }
                                    />

                                    <div className="min-w-0">
                                      <div className="font-medium text-gray-900 line-clamp-2">
                                        {lang ===
                                        "AR"
                                          ? product.name_ar ||
                                            product.name_en
                                          : product.name_en}
                                      </div>

                                      <div className="text-xs text-gray-500">
                                        Etika ID:{" "}
                                        {
                                          product.source_product_id
                                        }
                                      </div>
                                    </div>
                                  </div>
                                </td>

                                <td className="px-4 py-2 text-sm">
                                  {
                                    product.public_id
                                  }
                                </td>

                                <td className="px-4 py-2 text-sm">
                                  {(
                                    product.categories ||
                                    []
                                  )
                                    .map(
                                      (category) =>
                                        lang ===
                                        "AR"
                                          ? category.name_ar ||
                                            category.name_en ||
                                            category.slug
                                          : category.name_en ||
                                            category.slug
                                    )
                                    .join(", ")}
                                </td>

                                <td className="px-4 py-2 text-sm font-medium">
                                  $
                                  {Number(
                                    product.base_price ||
                                      0
                                  ).toFixed(2)}
                                </td>

                                <td className="px-4 py-2 text-sm">
                                  {product.stock}
                                </td>

                                <td className="px-4 py-2 text-sm">
                                  {
                                    product.variants_count
                                  }
                                </td>

                                <td className="px-4 py-2">
                                  <span
                                    className={`inline-flex px-2.5 py-1 text-xs rounded-full border ${status.className}`}
                                  >
                                    {status.label}
                                  </span>
                                </td>
                              </tr>
                            );
                          }
                        )
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="md:hidden p-3 space-y-3">
                  {loading ? (
                    [...Array(5)].map(
                      (_, index) => (
                        <div
                          key={index}
                          className="h-28 rounded-xl bg-gray-100 animate-pulse"
                        />
                      )
                    )
                  ) : paginatedProducts.length ===
                    0 ? (
                    <div className="py-10 text-center">
                      <FaBoxOpen className="mx-auto text-3xl text-gray-300 mb-3" />
                      <p className="text-gray-500">
                        {TEXT.empty}
                      </p>
                    </div>
                  ) : (
                    paginatedProducts.map(
                      (product) => {
                        const status =
                          getStatus(product);

                        return (
                          <div
                            key={
                              product.source_product_id
                            }
                            className="border border-gray-200 rounded-xl p-3"
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                disabled={
                                  !product.ready_to_import
                                }
                                checked={selectedIds.includes(
                                  product.source_product_id
                                )}
                                onChange={() =>
                                  toggleProduct(
                                    product
                                  )
                                }
                                className="mt-3 w-4 h-4 accent-gray-800 disabled:opacity-30"
                              />

                              <ProductThumbnail
                                src={product.image}
                                name={
                                  product.name_en
                                }
                              />

                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm text-gray-900">
                                  {lang === "AR"
                                    ? product.name_ar ||
                                      product.name_en
                                    : product.name_en}
                                </div>

                                <div className="text-xs text-gray-500 mt-1">
                                  #{product.public_id} ·{" "}
                                  {(
                                    product.categories ||
                                    []
                                  )
                                    .map(
                                      (category) =>
                                        category.slug
                                    )
                                    .join(", ")}
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-2 mt-3 text-center text-xs">
                              <div className="bg-gray-50 rounded-lg p-2">
                                <div className="text-gray-500">
                                  {TEXT.price}
                                </div>
                                <div className="font-semibold">
                                  $
                                  {Number(
                                    product.base_price ||
                                      0
                                  ).toFixed(2)}
                                </div>
                              </div>

                              <div className="bg-gray-50 rounded-lg p-2">
                                <div className="text-gray-500">
                                  {TEXT.stock}
                                </div>
                                <div className="font-semibold">
                                  {product.stock}
                                </div>
                              </div>

                              <div className="bg-gray-50 rounded-lg p-2">
                                <div className="text-gray-500">
                                  {TEXT.variants}
                                </div>
                                <div className="font-semibold">
                                  {
                                    product.variants_count
                                  }
                                </div>
                              </div>
                            </div>

                            <div className="mt-3">
                              <span
                                className={`inline-flex px-2.5 py-1 text-xs rounded-full border ${status.className}`}
                              >
                                {status.label}
                              </span>
                            </div>
                          </div>
                        );
                      }
                    )
                  )}
                </div>

                <div className="px-4 py-1 border-t border-[#e3e3e3] bg-[#f6f6f7] rounded-b-[14px]">
  <div
    className={`flex items-center justify-between text-sm text-gray-600 ${
      lang === "AR"
        ? "flex-row-reverse"
        : ""
    }`}
  >
    <span className="text-gray-500 text-sm">
      {filteredProducts.length}{" "}
      {lang === "AR" ? "منتجات" : "products"} ·{" "}
      {selectedIds.length} {TEXT.selected}
    </span>

    <div className="flex items-center gap-0.5">
      <button
        onClick={() =>
          goToImportProductsPage((current) =>
            Math.max(1, current - 1)
          )
        }
        disabled={page === 1}
        className={`w-7 h-7 flex font-bold items-center justify-center rounded-md border bg-gray-200 ${
          page === 1
            ? "opacity-40 cursor-not-allowed"
            : "hover:bg-gray-300"
        }`}
      >
        {"<"}
      </button>

      <button
        onClick={() =>
          goToImportProductsPage((current) =>
            Math.min(
              totalPages,
              current + 1
            )
          )
        }
        disabled={page === totalPages}
        className={`w-7 h-7 flex items-center font-bold justify-center rounded-md border bg-gray-200 ${
          page === totalPages
            ? "opacity-40 cursor-not-allowed"
            : "hover:bg-gray-300"
        }`}
      >
        {">"}
      </button>

      <span className="mx-2 text-gray-600 font-bold">
        {page}-{totalPages}
      </span>
    </div>
  </div>
</div>
              </div>
            </>
          )}
        </main>
      </div>

      {resultModal && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4">
          <div
            className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden"
            dir={lang === "AR" ? "rtl" : "ltr"}
          >
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {TEXT.importSelected}
              </h2>

              <button
                onClick={() =>
                  setResultModal(null)
                }
                className="w-8 h-8 rounded-full hover:bg-gray-100"
              >
                ✕
              </button>
            </div>

            <div className="p-5">
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <FaCheck className="text-green-600 mb-2" />
                  <div className="text-2xl font-semibold text-green-700">
                    {resultModal.imported_count ||
                      0}
                  </div>
                  <div className="text-xs text-green-700">
                    {TEXT.successful}
                  </div>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <FaExclamationTriangle className="text-red-600 mb-2" />
                  <div className="text-2xl font-semibold text-red-700">
                    {resultModal.failed_count ||
                      0}
                  </div>
                  <div className="text-xs text-red-700">
                    {TEXT.failed}
                  </div>
                </div>
              </div>

              {resultModal.failed?.length >
                0 && (
                <div className="max-h-56 overflow-y-auto border rounded-xl divide-y">
                  {resultModal.failed.map(
                    (item) => (
                      <div
                        key={
                          item.source_product_id
                        }
                        className="px-4 py-3"
                      >
                        <div className="font-medium text-sm">
                          Etika product{" "}
                          {
                            item.source_product_id
                          }
                        </div>

                        <div className="text-xs text-red-600 mt-1">
                          {item.error}
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t flex justify-end">
              <button
                onClick={() =>
                  setResultModal(null)
                }
                className="h-9 px-4 bg-[#1f1f1f] text-white rounded-lg"
              >
                {TEXT.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminImportProducts;
